import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Clock, Menu, X } from "lucide-react";
import { CONTACT } from "@/data/site";
import Logo from "@/components/Logo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Consumer & Legal Resources", to: "/resources" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, setOpen]);

  return (
    <header className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="bg-[#0B1B3D] text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-1 px-4 py-2 text-sm sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 font-medium">
            <Clock className="h-4 w-4 text-[#D4AF37]" />
            <span className="tracking-wide">24 Hour Delivery</span>
          </div>
          <a
            href={CONTACT.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 font-semibold transition-colors duration-200 hover:text-[#D4AF37]"
          >
            <Phone className="h-4 w-4 text-[#D4AF37]" />
            <span>{CONTACT.phone}</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo variant="navy" />

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={({ isActive }) =>
                  `relative text-[15px] font-medium transition-colors duration-200 hover:text-[#0B1B3D] ${
                    isActive ? "text-[#0B1B3D]" : "text-slate-500"
                  } after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:bg-[#D4AF37] after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/services"
              data-testid="header-cta-button"
              className="rounded-md bg-[#0B1B3D] px-5 py-2.5 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#14284f]"
            >
              View Services
            </Link>
          </nav>

          <button
            className="lg:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6 text-[#0B1B3D]" /> : <Menu className="h-6 w-6 text-[#0B1B3D]" />}
          </button>
        </div>

        {open && (
          <div className="border-t border-slate-100 bg-white lg:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-3 sm:px-6">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={({ isActive }) =>
                    `border-b border-slate-100 py-3 text-[15px] font-medium ${
                      isActive ? "text-[#0B1B3D]" : "text-slate-600"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
